import { API_URL } from '../services/api';

/**
 * Stored documents in the portal.
 *
 * Documents are never read from blob storage directly. Each one comes through
 * a backend route that checks the signed-in customer owns it, then streams it
 * with the server's managed identity. Those routes authenticate by cookie, so
 * both an axios call and a plain navigation to one work.
 *
 * A fetched document becomes an object URL on THIS origin — and the API
 * accepts credentialed calls from this origin. Something a browser executes,
 * such as an uploaded .html or .svg, could act as the customer from here. So
 * only types that render without running code keep their type; anything else
 * is made inert and can only be downloaded.
 */

const PREVIEWABLE_TYPES = new Set([
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
  'text/plain',
  'text/csv',
]);

/** True for a type that renders without executing anything. */
export function isPreviewable(type) {
  return !!type && PREVIEWABLE_TYPES.has(String(type).split(';')[0].trim().toLowerCase());
}

/** A Blob that keeps its type only when that type is safe to render. */
export function safeBlob(data, type) {
  const clean = String(type || '').split(';')[0].trim().toLowerCase();
  return new Blob([data], { type: isPreviewable(clean) ? clean : 'application/octet-stream' });
}

/**
 * Absolute URL for a backend document route, for navigations such as
 * window.open — a bare "/api/..." would land on the portal's own static server.
 * Absolute URLs pass only if they are this API's; anything else (an old public
 * storage link, say) returns null rather than being opened.
 */
export function apiUrl(path) {
  if (!path) return null;
  const base = API_URL.replace(/\/+$/, '');
  if (/^https?:\/\//i.test(path)) return path.startsWith(`${base}/`) ? path : null;
  return `${base}/${String(path).replace(/^\/+/, '')}`;
}
